"use client";

import { useEffect } from "react";
import Link from "next/link";
import { content } from "@/config/content";
import { siteConfig } from "@/config/siteConfig";
import { ToastProvider, useToast } from "@/components/shared/Toast";
import { useCart } from "@/components/shared/CartProvider";
import ImagePlaceholder from "@/components/shared/ImagePlaceholder";
import ArtworkCard from "@/components/public/ArtworkCard";
import { useSiteSettings } from "@/lib/useSiteSettings";
import ShareButton from "@/components/shared/ShareButton";
import ShareWelcomeBanner from "@/components/shared/ShareWelcomeBanner";
import { buildSharePayload } from "@/lib/shareUrl";
import { trackViewItem, trackAddToCart } from "@/lib/analytics";

import { useRouter } from "next/navigation";

function ArtworkDetail({ artwork, related }) {
  const showToast = useToast();
  const router = useRouter();
  const { addItem, items } = useCart();
  const isAvailable = artwork.status === "available";
  const alreadyInCart = items.some((item) => item.id === artwork.id);
  const { settings } = useSiteSettings("pricing");
  const { settings: art } = useSiteSettings("artwork");
  const fileDownload = Number(settings.price_file_download || 2500);
  const displayPrice =
    Number(artwork.price) > 0 ? Number(artwork.price) : fileDownload;

  // GA4/Meta view_item — fire once per artwork view (resolved display price).
  useEffect(() => {
    if (!artwork?.id) return;
    trackViewItem({ id: artwork.id, name: artwork.name }, displayPrice);
  }, [artwork?.id, artwork?.name, displayPrice]);

  const handleClaim = () => {
    addItem(artwork);
    trackAddToCart({ id: artwork.id, name: artwork.name }, displayPrice);
    showToast(art.artwork_add_to_cart_toast || "已加入購物車 ✨");
  };

  return (
    <main style={{ backgroundColor: "var(--color-bg)", minHeight: "100vh" }}>
      <ShareWelcomeBanner kind="artwork" status={artwork.status} />
      <div
        className="max-w-5xl mx-auto"
        style={{ padding: "var(--spacing-pagePadding)" }}
      >
        <Link
          href="/"
          className="inline-block mb-6 text-sm hover-shift-left"
          style={{ color: "var(--color-textSecondary)" }}
        >
          ← 回到藝廊
        </Link>

        {/* Two-column layout on desktop, single column on mobile */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10 mb-10">
          {/* Left: Image */}
          <div className="md:sticky md:top-20 md:self-start">
            <ImagePlaceholder
              name={artwork.name}
              image={artwork.image}
              artworkId={artwork.id}
              aspectRatio="4/5"
              className="w-full"
              style={{ borderRadius: "var(--radius-md)" }}
              priority
              sizes="(max-width: 768px) 100vw, 50vw"
            />
          </div>

          {/* Right: Info */}
          <div className="flex flex-col">
            <div className="flex items-start justify-between gap-3 mb-3">
              <h1
                className="text-2xl md:text-3xl font-bold"
                style={{
                  fontFamily: "var(--font-heading)",
                  color: "var(--color-text)",
                }}
              >
                {artwork.name}
              </h1>
              <ShareButton
                payload={buildSharePayload({ artwork, kind: "artwork" })}
                variant="iconSolid"
                size="md"
                placement="page"
                className="shrink-0"
              />
            </div>

            <p
              className="text-sm leading-relaxed mb-6"
              style={{ color: "var(--color-textSecondary)" }}
            >
              {artwork.description}
            </p>

            {artwork.suggestedPositions && (
              <div className="mb-6">
                <p
                  className="text-xs mb-2"
                  style={{ color: "var(--color-textMuted)" }}
                >
                  {content.gallery.suggestedPosition}
                </p>
                <div className="flex flex-wrap gap-2">
                  {artwork.suggestedPositions.map((pos) => (
                    <span
                      key={pos}
                      className="text-xs px-3 py-1"
                      style={{
                        backgroundColor: "var(--color-bgSecondary)",
                        color: "var(--color-textSecondary)",
                        borderRadius: "var(--radius-full)",
                      }}
                    >
                      {pos}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Decorative accent line */}
            <div
              className="w-12 mb-4"
              style={{
                height: "1px",
                background: "var(--color-accent)",
                opacity: 0.2,
              }}
            />

            {/* Price & action card */}
            <div
              id="cta"
              className="flex flex-col gap-3 p-5"
              style={{
                backgroundColor: "var(--color-cardBg)",
                borderRadius: "var(--radius-md)",
                border: "1px solid var(--color-cardBorder)",
              }}
            >
              <span
                className="text-xl md:text-2xl font-bold"
                style={{ color: "var(--color-accent)" }}
              >
                {content.gallery.pricePrefix}
                {displayPrice.toLocaleString()}
              </span>

              <p
                className="text-xs mt-1"
                style={{ color: "var(--color-accent)", fontWeight: 500 }}
              >
                此為線上圖檔授權費用，不含實體刺青操作
              </p>

              <p className="text-sm opacity-60 mt-2">
                想要 PDF 版本或客製化設計？請加 LINE 洽詢
              </p>

              {isAvailable ? (
                alreadyInCart ? (
                  <span
                    className="w-full px-6 py-3 text-sm text-center font-medium"
                    style={{
                      backgroundColor: "var(--color-bgSecondary)",
                      color: "var(--color-textMuted)",
                      borderRadius: "var(--radius-full)",
                    }}
                  >
                    {art.artwork_in_cart || "已在購物車中"}
                  </span>
                ) : (
                  <button
                    onClick={handleClaim}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-medium text-white text-center transition-transform active:scale-95"
                    style={{
                      backgroundColor: "var(--color-accent)",
                      borderRadius: "var(--radius-full)",
                    }}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="9" cy="21" r="1" />
                      <circle cx="20" cy="21" r="1" />
                      <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
                    </svg>
                    {art.artwork_add_to_cart || "加入購物車"}
                  </button>
                )
              ) : (
                <span
                  className="w-full px-6 py-3 text-sm text-center"
                  style={{
                    backgroundColor: "var(--color-bgSecondary)",
                    color: "var(--color-textMuted)",
                    borderRadius: "var(--radius-full)",
                  }}
                >
                  {content.gallery.claimedButton}
                </span>
              )}

              <div className="flex justify-center pt-1">
                <ShareButton
                  payload={buildSharePayload({ artwork, kind: "artwork" })}
                  variant="pill"
                  size="sm"
                  placement="page"
                  emoji="🐾"
                  label="分享這張給姐妹"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Related artworks — full width below */}
        {related.length > 0 && (
          <div id="related">
            <div className="flex items-center gap-3 mb-4">
              <span
                style={{
                  width: "16px",
                  height: "2px",
                  background: "var(--color-accent)",
                  opacity: 0.5,
                  borderRadius: "1px",
                }}
              />
              <h2
                className="text-lg font-bold"
                style={{
                  fontFamily: "var(--font-heading)",
                  color: "var(--color-text)",
                }}
              >
                {art.artwork_related_heading || "你可能也喜歡"}
              </h2>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
              {related.map((a) => (
                <ArtworkCard
                  key={a.id}
                  artwork={a}
                  onClick={() => router.push(`/artwork/${a.id}`)}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  );
}

export default function ArtworkDetailClient({ artwork, related }) {
  return (
    <ToastProvider>
      <ArtworkDetail artwork={artwork} related={related} />
    </ToastProvider>
  );
}
